import { useState } from 'react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react'
import FlexBreakdownCharts from '../FlexBreakdownCharts'

import type { FlexMessageAnalyticsResponse } from '../../types'

interface FlexAnalyticsTableProps {
	data: FlexMessageAnalyticsResponse[]
	isLoading?: boolean
	title?: string
}

type SortField =
	| 'flexMessageName'
	| 'totalRecipients'
	| 'openRate'
	| 'playRate'
	| 'lastCalculatedAt'
	| null
type SortOrder = 'asc' | 'desc'

function formatNumber(value: number): string {
	return value.toLocaleString('th-TH')
}

function formatRate(rate: number | undefined): string {
	if (rate === undefined || rate === null) return '0.00%'
	return `${rate.toFixed(2)}%`
}

export default function FlexAnalyticsTable({
	data,
	isLoading,
	title = 'รายละเอียด Flex Message',
}: FlexAnalyticsTableProps) {
	const [sortField, setSortField] = useState<SortField>('lastCalculatedAt')
	const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortOrder('desc')
		}
	}

	const toggleRowExpand = (id: string) => {
		const newExpanded = new Set(expandedRows)
		if (newExpanded.has(id)) {
			newExpanded.delete(id)
		} else {
			newExpanded.add(id)
		}
		setExpandedRows(newExpanded)
	}

	// Sort data
	const sortedData = [...data].sort((a, b) => {
		if (!sortField) return 0

		let aValue: number | string
		let bValue: number | string

		switch (sortField) {
			case 'flexMessageName':
				aValue = a.flexMessageName || ''
				bValue = b.flexMessageName || ''
				break
			case 'totalRecipients':
				aValue = a.totalRecipients
				bValue = b.totalRecipients
				break
			case 'openRate':
				aValue = a.openRate ?? 0
				bValue = b.openRate ?? 0
				break
			case 'playRate':
				aValue = a.playRate ?? 0
				bValue = b.playRate ?? 0
				break
			case 'lastCalculatedAt':
				aValue = new Date(a.lastCalculatedAt).getTime()
				bValue = new Date(b.lastCalculatedAt).getTime()
				break
			default:
				return 0
		}

		if (typeof aValue === 'string' && typeof bValue === 'string') {
			return sortOrder === 'asc'
				? aValue.localeCompare(bValue)
				: bValue.localeCompare(aValue)
		}

		return sortOrder === 'asc'
			? (aValue as number) - (bValue as number)
			: (bValue as number) - (aValue as number)
	})

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-muted-foreground">ไม่พบข้อมูล Flex Message</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{title} ({data.length})
				</CardTitle>
				<p className="text-sm text-muted-foreground">
					คลิกแถวเพื่อดูรายละเอียด Breakdown
				</p>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[40px]"></TableHead>
								<TableHead>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('flexMessageName')}
										className="h-8"
									>
										ชื่อ Flex Message
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('totalRecipients')}
										className="h-8"
									>
										ผู้รับ
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">เปิดดู</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('openRate')}
										className="h-8"
									>
										Open %
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">เล่น</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('playRate')}
										className="h-8"
									>
										Play %
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('lastCalculatedAt')}
										className="h-8"
									>
										อัปเดตล่าสุด
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sortedData.map((item) => {
								const isExpanded = expandedRows.has(item.id)

								return (
									<>
										<TableRow
											key={item.id}
											className="cursor-pointer hover:bg-muted/50"
											onClick={() => toggleRowExpand(item.id)}
										>
											<TableCell>
												{isExpanded ? (
													<ChevronUp className="h-4 w-4" />
												) : (
													<ChevronDown className="h-4 w-4" />
												)}
											</TableCell>
											<TableCell className="font-medium">
												{item.flexMessageName}
											</TableCell>
											<TableCell className="text-right">
												{formatNumber(item.totalRecipients)}
											</TableCell>
											<TableCell className="text-right">
												{formatNumber(item.uniqueOpeners)}
											</TableCell>
											<TableCell className="text-right">
												<span className="font-semibold text-blue-600 dark:text-blue-400">
													{formatRate(item.openRate)}
												</span>
											</TableCell>
											<TableCell className="text-right">
												{formatNumber(item.uniquePlayers)}
											</TableCell>
											<TableCell className="text-right">
												<span className="font-semibold text-green-600 dark:text-green-400">
													{formatRate(item.playRate)}
												</span>
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{format(
													new Date(item.lastCalculatedAt),
													'dd MMM yyyy HH:mm',
													{ locale: th },
												)}
											</TableCell>
										</TableRow>

										{/* Expanded Breakdown Details */}
										{isExpanded && (
											<TableRow key={`${item.id}-expanded`}>
												<TableCell colSpan={8} className="bg-muted/20 p-4">
													<FlexBreakdownCharts
														walletTypeBreakdown={item.walletTypeBreakdown}
														balanceRangeBreakdown={item.balanceRangeBreakdown}
														gameBreakdown={item.gameBreakdown}
													/>
												</TableCell>
											</TableRow>
										)}
									</>
								)
							})}
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	)
}
